import { yupResolver } from '@hookform/resolvers/yup';
import { Stack, styled, Grid } from '@mui/material';
import { Fragment, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { editTeam, ITeam } from 'wisestep-api';
import {
  Item,
  TagsInput,
  WsButton,
  WsButtonGroup,
  WsDrawer,
  WsTypography,
  WsTypoVariants,
  IUserDTO,
} from 'wisestep-base';

import { useUserContext } from '../../../context';
import { useNotificationStore } from '../../../utils/NotificationStore';
import { teamSettingsDefaultValues } from '../helper/defaultValues';
import { teamSettingsSchema } from '../helper/validation';

const TeamSettingsItem = styled(Item)(() => ({
  display: 'flex',
  justifyContent: 'space-between',
}));

export interface ISettingsData {
  jobShareNotificationTo: string[];
  jobClosedNotificationTo: string[];
  jobInsightUpdateNotificationTo: string[];
  candidateSubmissionNotificationTo: string[];
  candidateStatusUpdateNotificationTo: string[];
  disableJobShareNotificationTo: string[];
  disableJobClosedNotificationTo: string[];
  disableJobInsightUpdateNotificationTo: string[];
  disableCandidateSubmissionNotificationTo: string[];
  disableCandidateStatusUpdateNotificationTo: string[];
}

const labels: string[] = [
  'When a Job is Shared',
  'When a Job is Closed',
  'Job insights update',
  'When a Candidate is Submitted',
  'When a Candidate Status is Update',
];

const communicationSettings = new Map();

communicationSettings.set('When a Job is Shared', 'jobShareNotificationTo');
communicationSettings.set('When a Job is Closed', 'jobClosedNotificationTo');
communicationSettings.set('Job insights update', 'jobInsightUpdateNotificationTo');
communicationSettings.set('When a Candidate is Submitted', 'candidateSubmissionNotificationTo');
communicationSettings.set(
  'When a Candidate Status is Update',
  'candidateStatusUpdateNotificationTo'
);

const notificationSettings = new Map();

notificationSettings.set('When a Job is Shared', 'disableJobShareNotificationTo');
notificationSettings.set('When a Job is Closed', 'disableJobClosedNotificationTo');
notificationSettings.set('Job insights update', 'disableJobInsightUpdateNotificationTo');
notificationSettings.set(
  'When a Candidate is Submitted',
  'disableCandidateSubmissionNotificationTo'
);
notificationSettings.set(
  'When a Candidate Status is Update',
  'disableCandidateStatusUpdateNotificationTo'
);

export interface ITeamSettingsProps {
  team: ITeam;
  open: boolean;
  handleClose: () => void;
}

const TeamSettings = ({ team, open, handleClose }) => {
  const [customSettingIndex, setCustomSettingIndex] = useState<number>(-1);
  const user = useUserContext().userDTO as IUserDTO;
  const setNotification = useNotificationStore((state) => state.setNotification);

  const methods = useForm({
    resolver: yupResolver(teamSettingsSchema),
    defaultValues: teamSettingsDefaultValues,
  });

  const saveSettings = async (data: ISettingsData) => {
    team.jobShareNotificationTo = data.jobShareNotificationTo.toString();
    team.jobClosedNotificationTo = data.jobClosedNotificationTo.toString();
    team.jobInsightUpdateNotificationTo = data.jobInsightUpdateNotificationTo.toString();
    team.candidateSubmissionNotificationTo = data.candidateSubmissionNotificationTo.toString();
    team.candidateStatusUpdateNotificationTo = data.candidateStatusUpdateNotificationTo.toString();
    team.disableJobShareNotificationTo = data.disableJobShareNotificationTo.toString();
    team.disableJobClosedNotificationTo = data.disableJobClosedNotificationTo.toString();
    team.disableJobInsightUpdateNotificationTo =
      data.disableJobInsightUpdateNotificationTo.toString();
    team.disableCandidateSubmissionNotificationTo =
      data.disableCandidateSubmissionNotificationTo.toString();
    team.disableCandidateStatusUpdateNotificationTo =
      data.disableCandidateStatusUpdateNotificationTo.toString();
    try {
      await editTeam(team);
      handleClose();
      setNotification({
        type: 'success',
        message: 'Settings saved successfully!',
      });
    } catch (e) {
      setNotification({ type: 'error', message: 'Something Went wrong try again after some time' });
    }
  };

  const setCustom = (
    setting:
      | 'jobShareNotificationTo'
      | 'jobClosedNotificationTo'
      | 'jobInsightUpdateNotificationTo'
      | 'candidateSubmissionNotificationTo'
      | 'candidateStatusUpdateNotificationTo'
  ) => {
    setCustomSettingIndex(-1);
    methods.setValue(setting, ['']);
  };

  const pushToUnsubscribedList = (index: number, label: string) => {
    const unsubscribedList: string[] = (
      (team[notificationSettings.get(label)] as string | undefined) ?? ''
    )
      ?.split(',')
      .filter((value) => value !== '');
    const indexOfCurrentUser = unsubscribedList.indexOf(user?.email ?? '');
    if (index !== 0) unsubscribedList.push(user?.email || '');
    else if (index === 0 && indexOfCurrentUser !== -1)
      unsubscribedList.splice(indexOfCurrentUser, 1);
    methods.setValue(notificationSettings.get(label), unsubscribedList);
  };

  return (
    <WsDrawer title="Team Settings" open={open} onClose={handleClose}>
      <FormProvider {...methods}>
        <Stack p={2} component="form" onSubmit={methods.handleSubmit(saveSettings)}>
          <WsTypography variant={WsTypoVariants.SUB_TITLE}>
            Communication settings for the team
          </WsTypography>
          {labels.map((label, key) => (
            <Fragment key={key}>
              <TeamSettingsItem>
                <Grid container direction="row" justifyContent={'end'}>
                  <Grid item xs={10}>
                    <WsTypography>{label}</WsTypography>
                  </Grid>
                  <Grid item xs>
                    <WsButtonGroup
                      inputTitle=""
                      buttonsList={['All', 'Custom']}
                      selectedIndexValue={(index) => {
                        index === 1
                          ? setCustomSettingIndex(key)
                          : setCustom(communicationSettings.get(label));
                      }}
                      defaultValue={team[communicationSettings.get(label)]?.length === 0 ? 0 : 1}
                    />
                  </Grid>
                </Grid>
              </TeamSettingsItem>
              {(team[communicationSettings.get(label)]?.length > 0 ||
                customSettingIndex === key) && (
                <TagsInput
                  name={communicationSettings.get(label)}
                  placeholder="Enter emails"
                  initialValue={
                    team[communicationSettings.get(label)]?.length > 0
                      ? Array(team[communicationSettings.get(label)])
                      : []
                  }
                  multiple={true}
                  onTagsChange={(tags) => methods.setValue(communicationSettings.get(label), tags)}
                />
              )}
            </Fragment>
          ))}
          <WsTypography variant={WsTypoVariants.SUB_TITLE}>
            Customize your notifications for the following events
          </WsTypography>
          {labels.map((label, key) => (
            <TeamSettingsItem key={key}>
              <Grid container direction="row" justifyContent={'end'}>
                <Grid item xs={8.8}>
                  <WsTypography>{label}</WsTypography>
                </Grid>
                <Grid item xs>
                  <WsButtonGroup
                    inputTitle=""
                    buttonsList={['Subscribe', 'Unsubscribe']}
                    selectedIndexValue={(index) => pushToUnsubscribedList(index, label)}
                    defaultValue={
                      (team[notificationSettings.get(label)] as string | undefined)
                        ?.split(',')
                        .includes(user?.email ?? '_')
                        ? 1
                        : 0
                    }
                  />
                </Grid>
              </Grid>
            </TeamSettingsItem>
          ))}
          <WsButton type="submit">Save Settings</WsButton>
        </Stack>
      </FormProvider>
    </WsDrawer>
  );
};

export default TeamSettings;
